import dayjs from "dayjs"
import { useCartDispatch, useCartState } from "hooks/useCartV2"
import { useRouter } from "next/router"
import { useFeatureFlagPayload } from "posthog-js/react"
import { useEffect, useMemo, useRef } from "react"
import { IFinalEvent } from "types/event"
import { FeatureFlags } from "types/FeatureFlags"
import {
    IDayType,
    ImprovedCartActionsEnum,
    ISelectedTicket,
} from "types/improved-booking"
import { ItimeSlot } from "types/ticket"
import { isEidPartyEvent } from "utils/helpers/constants"
import { isEventExpired } from "utils/helpers/EventHelper"

type FeatureFlagPayload = {
    events?: string[]
}

function buildDefaultDayTypes(
    event: IFinalEvent,
    timeslot: ItimeSlot
): IDayType[] {
    const masterTicketsMap = new Map(
        event.availableTickets.map((ticket) => [ticket._id, ticket])
    )
    const resolvedTypes: IDayType[] = []

    for (const slotType of timeslot.types || []) {
        const masterTicketDetails = masterTicketsMap.get(slotType.type_id)

        if (!masterTicketDetails) {
            continue
        }

        resolvedTypes.push({
            _id: slotType._id,
            limit: slotType.limit,
            wait_list: slotType.wait_list,
            type_id: masterTicketDetails._id,
            ar_title: masterTicketDetails.ar_title,
            en_title: masterTicketDetails.en_title,
            ar_description: masterTicketDetails.ar_description,
            en_description: masterTicketDetails.en_description,
            color: masterTicketDetails.color,
            discount: masterTicketDetails.discount,
            min: masterTicketDetails.min,
            max: masterTicketDetails.max,
            must_select_seat: masterTicketDetails.must_select_seat,
            price: masterTicketDetails.price,
            promotion: masterTicketDetails.promotion,
            group: undefined,
        })
    }

    return resolvedTypes
}

export function useBookingPageInitialization({
    event,
    tickets,
    eventId,
    isLoading,
}: {
    event?: IFinalEvent
    tickets: ItimeSlot[]
    eventId: string
    isLoading: boolean
}) {
    const cartDispatch = useCartDispatch()
    const cartState = useCartState()
    const router = useRouter()
    const hasInitializedCart = useRef(false)
    const initializedEventRef = useRef<string | null>(null)
    const featureFlagPayload = useFeatureFlagPayload(
        FeatureFlags.BOOKING_V1
    ) as FeatureFlagPayload

    const flagEvents = featureFlagPayload?.events || []
    const shouldRedirectToEventPage = useMemo(() => {
        if (!event) return false
        return (
            (flagEvents.length > 0 && flagEvents.includes(eventId)) ||
            isEventExpired(event)
        )
    }, [event, eventId, flagEvents])
    const hasExistingSelectionForCurrentEvent = useMemo(() => {
        if (!event) return false

        const knownEventIds = [cartState.event, cartState.event_id].filter(
            (value): value is string =>
                typeof value === "string" && value.length > 0
        )
        const matchesCurrentEvent =
            knownEventIds.includes(eventId) || knownEventIds.includes(event._id)

        if (!matchesCurrentEvent) {
            return false
        }

        return (
            !!cartState.selectedDays ||
            cartState.selectedTickets.length > 0 ||
            cartState.selectedSeats.length > 0 ||
            cartState.selectedExtras.length > 0
        )
    }, [
        cartState.event,
        cartState.event_id,
        cartState.selectedDays,
        cartState.selectedTickets.length,
        cartState.selectedSeats.length,
        cartState.selectedExtras.length,
        event,
        eventId,
    ])
    const hasExistingSelectionForDifferentEvent = useMemo(() => {
        if (!event) return false

        const knownEventIds = [
            cartState.event,
            cartState.event_id,
            cartState.eventSlug,
        ].filter(
            (value): value is string =>
                typeof value === "string" && value.length > 0
        )
        const matchesCurrentEvent =
            knownEventIds.includes(eventId) || knownEventIds.includes(event._id)

        if (matchesCurrentEvent || knownEventIds.length === 0) {
            return false
        }

        return true
    }, [
        cartState.event,
        cartState.event_id,
        cartState.eventSlug,
        event,
        eventId,
    ])

    useEffect(() => {
        if (event?._id && initializedEventRef.current !== event._id) {
            hasInitializedCart.current = false
            initializedEventRef.current = event._id
        }
    }, [event?._id])

    useEffect(() => {
        if (shouldRedirectToEventPage) {
            router.push(`/events/${eventId}`)
        }
    }, [shouldRedirectToEventPage, eventId, router])

    useEffect(() => {
        if (!router.isReady || isLoading || !event || !event.availableTickets) {
            return
        }

        if (hasInitializedCart.current) {
            return
        }

        hasInitializedCart.current = true

        if (tickets.length === 0) {
            router.push(`/events/${eventId}`)
            return
        }

        const hasExpiredTimeslots =
            tickets.length > 0 &&
            tickets.every((slot) => new Date(slot.end_slot) < new Date())
        const eventHasEnded =
            !!event.event_date?.ending_date &&
            new Date(event.event_date.ending_date) < new Date()

        if (!eventHasEnded && hasExpiredTimeslots) {
            router.push(`/events/${eventId}`)
            return
        }

        if (hasExistingSelectionForDifferentEvent) {
            cartDispatch({ type: ImprovedCartActionsEnum.CLEAR_CART })
        }

        cartDispatch({
            type: ImprovedCartActionsEnum.ADD_EVENT,
            payload: { event: eventId, slug: router.query.eventid as string },
        })

        // Preserve in-progress selections when the user returns from checkout.
        // Rebootstrapping the day/timeslot here would clear selected tickets.
        // Also validate the existing selection against fresh event/ticket data to prevent stale data issues.
        if (hasExistingSelectionForCurrentEvent) {
            if (cartState.selectedDays) {
                const selectedTimeslot = tickets.find(
                    (t) => t._id === cartState.selectedDays?.timeslot_id
                )
                const isTimeslotExpired = selectedTimeslot
                    ? new Date(selectedTimeslot.end_slot) < new Date()
                    : true

                if (!selectedTimeslot || isTimeslotExpired) {
                    // Selected timeslot is invalid or expired, clear the cart
                    cartDispatch({ type: ImprovedCartActionsEnum.CLEAR_CART })
                    hasInitializedCart.current = false
                    return
                }

                // Validate and update selectedTickets
                if (cartState.selectedTickets.length > 0) {
                    const updatedTickets: ISelectedTicket[] = []
                    let hasChanges = false

                    for (const ticket of cartState.selectedTickets) {
                        const ticketType = selectedTimeslot.types.find(
                            (type) => {
                                const typeId =
                                    typeof type.type_id === "object" &&
                                    type.type_id !== null
                                        ? (type.type_id as any)._id
                                        : type.type_id

                                return typeId === ticket.type_id
                            }
                        )

                        // If ticket type is no longer available or sold out, remove it
                        if (!ticketType || ticketType.limit <= 0) {
                            hasChanges = true
                            continue
                        }

                        // Check if price or other details changed
                        const masterTicketDetails = event.availableTickets.find(
                            (t) => t._id === ticket.type_id
                        )

                        const currentPrice = ticketType.price
                        if (ticket.price !== currentPrice) {
                            hasChanges = true
                        }

                        updatedTickets.push({
                            ...ticket,
                            price: currentPrice,
                            ar_title:
                                masterTicketDetails?.ar_title ||
                                ticket.ar_title,
                            en_title:
                                masterTicketDetails?.en_title ||
                                ticket.en_title,
                        })
                    }

                    if (hasChanges) {
                        cartDispatch({
                            type: ImprovedCartActionsEnum.ADD_ALL_SELECTED_TICKETS,
                            payload: updatedTickets,
                        })
                    }
                }

                // Update selectedDays.types with fresh data
                const freshDayTypes = buildDefaultDayTypes(
                    event,
                    selectedTimeslot
                )
                const typesChanged =
                    JSON.stringify(cartState.selectedDays.types) !==
                    JSON.stringify(freshDayTypes)
                if (typesChanged) {
                    cartDispatch({
                        type: ImprovedCartActionsEnum.ADD_DAY,
                        payload: {
                            ...cartState.selectedDays,
                            types: freshDayTypes,
                            seats_season_id:
                                event.seatsio?.workspace_id ||
                                selectedTimeslot.seats_season_id ||
                                "",
                            allow_using_coupon:
                                selectedTimeslot.allow_using_coupon,
                        },
                    })
                }
            }
            return
        }

        if (isEidPartyEvent(eventId)) {
            const hasMultipleTimeslots = tickets.length > 1

            if (!hasMultipleTimeslots) {
                const selectedTimeslot = tickets[0]
                const defaultDayTypes = buildDefaultDayTypes(
                    event,
                    selectedTimeslot
                )

                cartDispatch({
                    type: ImprovedCartActionsEnum.ADD_DAY,
                    payload: {
                        dayName: dayjs(selectedTimeslot.start_slot).format(
                            "dddd"
                        ),
                        dayNumber: dayjs(selectedTimeslot.start_slot).date(),
                        monthName: dayjs(selectedTimeslot.start_slot).format(
                            "MMMM"
                        ),
                        monthNum:
                            dayjs(selectedTimeslot.start_slot).month() + 1,
                        yearNum: dayjs(selectedTimeslot.start_slot)
                            .year()
                            .toString(),
                        start: new Date(selectedTimeslot.start_slot),
                        end: new Date(selectedTimeslot.end_slot),
                        timeslot_id: selectedTimeslot._id,
                        types: defaultDayTypes,
                        seats_season_id:
                            event.seatsio?.workspace_id ||
                            selectedTimeslot.seats_season_id ||
                            "",
                        allow_using_coupon: selectedTimeslot.allow_using_coupon,
                    },
                })
            }

            return
        }
    }, [
        router,
        isLoading,
        event,
        tickets,
        eventId,
        cartDispatch,
        hasExistingSelectionForCurrentEvent,
        hasExistingSelectionForDifferentEvent,
        cartState.selectedDays?.types,
        cartState.selectedDays?.timeslot_id,
        cartState.selectedTickets,
        cartState.selectedDays,
    ])
}
